import { TosModalLegalError } from '@ltvco/refresh-lib/payments';
import {
  createContext,
  useContext,
  type ComponentProps,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { HeaderParent } from 'components/HeaderParent/HeaderParent';
import { useSearchSuccessRedirect } from 'hooks/useSearchSuccessRedirect';
import { Fraud } from 'routes/Dashboard/Fraud';
import { Report } from 'routes/Report';

import type { SearchData } from '@ltvco/refresh-lib/v1';

type SearchSuccessHandler = (searchData: SearchData) => void;

const SearchSuccessRedirectContext = createContext<SearchSuccessHandler | null>(
  null
);

/**
 * Must render inside GrowthBook so feature-flag routing can resolve variants.
 */
export const SearchSuccessRedirectProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const navigate = useNavigate();
  const handleSearchSuccess = useSearchSuccessRedirect(navigate);

  return (
    <SearchSuccessRedirectContext.Provider value={handleSearchSuccess}>
      {children}
    </SearchSuccessRedirectContext.Provider>
  );
};

export const useSearchSuccessRedirectHandler = (): SearchSuccessHandler => {
  const handler = useContext(SearchSuccessRedirectContext);

  if (handler === null) {
    throw new Error(
      'useSearchSuccessRedirectHandler must be used within SearchSuccessRedirectProvider'
    );
  }

  return handler;
};

type HeaderParentSearchProps = Omit<
  ComponentProps<typeof HeaderParent>,
  'handleSearchSuccess'
>;

export const HeaderParentWithSearchSuccess = (
  props: HeaderParentSearchProps
) => {
  const handleSearchSuccess = useSearchSuccessRedirectHandler();

  return <HeaderParent {...props} handleSearchSuccess={handleSearchSuccess} />;
};

type FraudSearchProps = Omit<ComponentProps<typeof Fraud>, 'onSearchSuccess'>;

export const FraudWithSearchSuccess = (props: FraudSearchProps) => {
  const onSearchSuccess = useSearchSuccessRedirectHandler();

  return <Fraud {...props} onSearchSuccess={onSearchSuccess} />;
};

type ReportSearchProps = Omit<ComponentProps<typeof Report>, 'onSearchSuccess'>;

export const ReportWithSearchSuccess = (props: ReportSearchProps) => {
  const onSearchSuccess = useSearchSuccessRedirectHandler();

  return <Report {...props} onSearchSuccess={onSearchSuccess} />;
};

type TosModalLegalErrorSearchProps = Omit<
  ComponentProps<typeof TosModalLegalError>,
  'handleSearchSuccess'
>;

export const TosModalLegalErrorWithSearchSuccess = (
  props: TosModalLegalErrorSearchProps
) => {
  const handleSearchSuccess = useSearchSuccessRedirectHandler();

  return (
    <TosModalLegalError {...props} handleSearchSuccess={handleSearchSuccess} />
  );
};
